import axios, { AxiosError } from 'axios';
import { CommonRootState } from '../combineReducers';
import { IClient } from '../interfaces/client';
import { OrganizationFeature } from '../interfaces/organization';
import { sendError } from '../sendError';
import { ThunkAction } from 'redux-thunk';

const fetchClientsRequest = () =>
  ({
    type: 'FETCH_CLIENTS_REQUEST'
  } as const);

const fetchClientsSuccess = (payload: IClient[]) =>
  ({
    type: 'FETCH_CLIENTS_SUCCESS',
    payload
  } as const);

const fetchClientsFailure = (error: AxiosError) => {
  sendError(error);
  return {
    type: 'FETCH_CLIENTS_FAILURE',
    error
  } as const;
};

const shouldFetchClients = ({ clients: { isFetching } }: CommonRootState) => !isFetching;

const fetchClients = (
  organizationId: string,
  organizationFeatures: OrganizationFeature[]
): ThunkAction<Promise<IClient[]>, CommonRootState, null, Actions> => dispatch => {
  return new Promise(async (resolve, reject) => {
    try {
      dispatch(fetchClientsRequest());

      const responses = await axios.all(
        organizationFeatures.map(featureFilter =>
          axios.get(`/api/v2/organizations/${organizationId}/clients`, {
            params: {
              featureFilter
            }
          })
        )
      );

      const data = responses
        .reduce((a, b) => a.concat(b.data), [])
        .filter(({ id }, i, a) => a.findIndex((e: IClient) => e.id === id) === i);

      dispatch(fetchClientsSuccess(data));
      resolve(data);
    } catch (error) {
      dispatch(fetchClientsFailure(error));
      reject(error);
    }
  });
};

export const fetchClientsIfNeeded = (
  organizationId: string,
  organizationFeatures: OrganizationFeature[]
): ThunkAction<void, CommonRootState, null, Actions> => (dispatch, getState) => {
  if (shouldFetchClients(getState())) {
    dispatch(fetchClients(organizationId, organizationFeatures));
  }
};

export type Actions =
  | ReturnType<typeof fetchClientsRequest>
  | ReturnType<typeof fetchClientsSuccess>
  | ReturnType<typeof fetchClientsFailure>;
